/**
 * A file shared into a project, as it appears inside an AGENT's transcript.
 *
 * When someone attaches a file to a project room, the server decrypts it, symlinks it
 * somewhere an agent can read, and injects a notice into every attached agent's pane
 * (`project_files.py:750`):
 *
 *     📎 A file was shared in this project: cute.jpg (782.5KB) — saved locally at
 *     /home/yuanyen/project-files/<project_id>/cute.jpg
 *     You can read/process it directly (cat / open the path above).
 *
 * So in Clean View the picture arrives as a sentence about a path. Correct for the
 * agent, which opens the file; useless to the person reading over its shoulder.
 *
 * WHAT THE PATH DOES AND DOES NOT CARRY. `_agent_blob_link` builds
 * `~/project-files/<project_id>/<basename(name)>`, so the project id is recoverable
 * and the file id is NOT — the link is named for humans, and the id lives only in the
 * project's own records. That is why resolving one costs a lookup rather than a
 * regex, and why this module returns what it can prove rather than a blob URL.
 *
 * The name is also not unique. `_agent_blob_link` removes an existing link before
 * creating the new one, so re-sharing a filename repoints the path at the newer file.
 * A resolver must therefore pick the MOST RECENT match, which is what the path itself
 * would resolve to — anything else would show one file while the agent read another.
 */

/** `.../project-files/<project_id>/<name>` — the id is a uuid4 with dashes. */
const NOTICE =
  /saved locally at (\/\S*?\/project-files\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/([^\s\n]+))/g

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif'])

export type ProjectFileRef = {
  /** The full sentence fragment matched, so a caller can strip exactly this much. */
  match: string
  path: string
  projectId: string
  name: string
  isImage: boolean
}

/**
 * Pull every shared-file notice out of a message.
 *
 * The surrounding sentence is deliberately LEFT IN PLACE. It carries the size and the
 * fact that a file was shared at all, and — unlike a `<note-file:>` tag, which this
 * app wrote itself — it is prose a person composed around. Removing it would edit
 * someone's message; the picture is added underneath instead.
 */
export function parseProjectFileNotices(text: string): ProjectFileRef[] {
  const out: ProjectFileRef[] = []
  for (const m of text.matchAll(NOTICE)) {
    const name = decodeURIComponent(m[3]!)
    const ext = (name.split('.').pop() ?? '').toLowerCase()
    out.push({
      match: m[1]!,
      path: m[1]!,
      projectId: m[2]!,
      name,
      isImage: IMAGE_EXT.has(ext),
    })
  }
  return out
}
